import React, { useEffect, useRef } from 'react';
import { Check } from 'lucide-react';

export function ServiceFeeFactor() {
  const sectionRef = useRef(null);
  
  const factors = [
    "Complexity of the procedure",
    "Time required for treatment",
    "Materials used",
    "Technology involved",
    "Preventive vs. restorative",
    "Your long-term goals"
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          // Animate title and description
          const title = document.querySelector('.factor-title');
          const description = document.querySelector('.factor-description');
          
          if (title) title.classList.add('animate-in');
          if (description) {
            setTimeout(() => {
              description.classList.add('animate-in');
            }, 200);
          }
          
          // Animate factors with staggered delay
          const factorItems = document.querySelectorAll('.factor-item');
          factorItems.forEach((item, index) => {
            setTimeout(() => {
              item.classList.add('animate-in');
            }, 300 + (index * 100));
          });
        }
      },
      { threshold: 0.2 }
    );
    
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    
    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  return (
    <section 
      ref={sectionRef}
      className="bg-gradient-to-b from-white to-teal-50 py-16 px-6 relative overflow-hidden"
    >
      {/* Decorative background elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-green-100 rounded-full filter blur-3xl opacity-20 transform translate-x-1/2 -translate-y-1/2"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-teal-100 rounded-full filter blur-3xl opacity-20 transform -translate-x-1/2 translate-y-1/3"></div>
      
      {/* SVG decorative elements */}
      <div className="absolute right-0 top-1/4 opacity-10 pointer-events-none">
        <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M60,30 C72,30 75,15 75,15 C75,15 78,30 90,30 C100,30 100,45 95,55 C90,65 80,70 75,85 C70,70 60,65 55,55 C50,45 50,30 60,30 Z" fill="#22d3ee" />
        </svg>
      </div>
      
      <div className="absolute left-10 bottom-1/4 opacity-10 pointer-events-none">
        <svg width="100" height="100" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M50,25 C62,25 65,10 65,10 C65,10 68,25 80,25 C90,25 90,40 85,50 C80,60 70,65 65,80 C60,65 50,60 45,50 C40,40 40,25 50,25 Z" fill="#84cc16" />
        </svg>
      </div>
      
      <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
        <h2 className="factor-title text-3xl font-bold bg-gradient-to-r from-teal-600 to-green-600 bg-clip-text text-transparent opacity-0 transform -translate-y-4 transition-all duration-700">
          How We Determine Your Fee
        </h2>
        
        <div className="w-24 h-1 bg-gradient-to-r from-teal-400 to-green-400 mx-auto rounded-full"></div>
        
        <p className="factor-description text-gray-700 max-w-2xl mx-auto opacity-0 transform -translate-y-4 transition-all duration-700">
          Your care is as unique as your smile. We consider several factors to ensure your treatment is fair, personalized, and worth every penny.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 text-left mt-12">
          {factors.map((item, i) => (
            <div 
              key={i} 
              className="factor-item bg-white shadow-lg p-5 rounded-xl text-gray-700 font-medium border-l-4 border-teal-400 hover:shadow-xl transition-all duration-300 opacity-0 transform translate-y-8"
              style={{ transitionProperty: 'opacity, transform', transitionDuration: '0.5s' }}
            >
              <div className="flex items-center space-x-3">
                <div className="flex-shrink-0 bg-gradient-to-br from-teal-400 to-green-400 text-white p-1 rounded-full">
                  <Check size={16} />
                </div>
                <span>{item}</span>
              </div>
            </div>
          ))}
        </div>
        
        <div className="factor-item mt-12 p-6 bg-gradient-to-r from-teal-500 to-green-500 rounded-xl shadow-lg text-white opacity-0 transform translate-y-8 transition-all duration-700 delay-700">
          <h3 className="text-xl font-semibold mb-2">Transparent Pricing, No Surprises</h3>
          <p>We'll always discuss all treatment options and associated costs before proceeding with any care.</p>
        </div>
      </div>
    </section>
  );
}

// Add this CSS to your global styles or component styles
const styles = `
  .factor-title.animate-in,
  .factor-description.animate-in,
  .factor-item.animate-in {
    opacity: 1;
    transform: translateY(0);
  }
  
  @keyframes pulse-gentle {
    0% { box-shadow: 0 0 0 0 rgba(45, 212, 191, 0.3); }
    70% { box-shadow: 0 0 0 10px rgba(45, 212, 191, 0); }
    100% { box-shadow: 0 0 0 0 rgba(45, 212, 191, 0); }
  }
`;

export default function StyledServiceFeeFactor() {
  return (
    <>
      <style>{styles}</style>
      <ServiceFeeFactor />
    </>
  );
}